'use client'

import React from 'react'

import { Download } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

import { CardV2 } from '../CardV2'

// ============================================================================
// Types
// ============================================================================

interface DocumentsSectionProps {
  /** File cards to render (use OpportunityFileCard) */
  children: React.ReactNode
  /** Number of files (for badge display) */
  fileCount: number
  /** Whether the section is currently highlighted (from useScrollHighlight) */
  isHighlighted: boolean
  /** Header title (default: "Documents") */
  title?: string
  /** Empty state message when no files */
  emptyMessage?: string
  /** Additional className for the container */
  className?: string
}

// ============================================================================
// Component
// ============================================================================

/**
 * DocumentsSection - Container for opportunity document files
 *
 * Built on CardV2 for consistent styling with highlight animation support
 * for scroll-to-files functionality.
 *
 * @example
 * ```tsx
 * <DocumentsSection
 *   fileCount={data.files.length}
 *   isHighlighted={highlightFiles}
 *   title="Solictation Documents"
 *   emptyMessage="No documents available for this solicitation."
 * >
 *   {data.files.map((file) => (
 *     <OpportunityFileCard key={file.id} file={file} />
 *   ))}
 * </DocumentsSection>
 * ```
 */
const DocumentsSection: React.FC<DocumentsSectionProps> = ({
  children,
  fileCount,
  isHighlighted,
  title = 'Documents',
  emptyMessage = 'No documents available.',
  className,
}) => {
  return (
    <CardV2
      variant="accent"
      className={twMerge(
        'mt-4',
        isHighlighted
          ? 'border-accent-400 shadow-[0_0_0_4px_rgba(99,102,241,0.15)] ring-1 ring-accent-400 transition-all duration-200 ease-in dark:border-accent-500 dark:ring-accent-500'
          : 'transition-all duration-1000 ease-out',
        className,
      )}
    >
      <div id="files-section">
        {/* Header matching CardV2.Header styling */}
        <div
          className={twMerge(
            'border-b border-primary-100 bg-primary-50/50 px-4 py-3 dark:border-primary-800 dark:bg-primary-800/50',
            isHighlighted && 'border-accent-200 dark:border-accent-700',
          )}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              {/* Icon matching CardV2.HeaderIcon accent variant */}
              <span
                className={twMerge(
                  'flex h-4 w-4 shrink-0 items-center justify-center text-accent-500 dark:text-accent-400 [&>svg]:h-full [&>svg]:w-full',
                  isHighlighted && 'text-accent-600 dark:text-accent-400',
                )}
              >
                <Download />
              </span>
              {/* Title matching CardV2.HeaderTitle styling */}
              <h3
                className={twMerge(
                  'text-sm font-bold uppercase tracking-wider text-primary-500 dark:text-primary-400',
                  isHighlighted && 'text-accent-700 dark:text-accent-400',
                )}
              >
                {title}
              </h3>
            </div>
            {/* Badge matching CardV2.HeaderBadge */}
            <span className="rounded-full border border-primary-200 bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-500 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-400">
              {fileCount} Files
            </span>
          </div>
        </div>
        {/* Content matching CardV2.Content */}
        <div className="max-h-[650px] space-y-2.5 overflow-y-auto p-4">
          {fileCount > 0 ? (
            children
          ) : (
            <div className="rounded-lg border border-dashed border-primary-200 bg-primary-50 py-12 text-center text-sm text-primary-500 dark:border-primary-700 dark:bg-primary-800/50 dark:text-primary-400">
              {emptyMessage}
            </div>
          )}
        </div>
      </div>
    </CardV2>
  )
}

DocumentsSection.displayName = 'DocumentsSection'

export { DocumentsSection }
export type { DocumentsSectionProps }
